import { Button, Col, Collapse, Row, Table, Tooltip, Space, Tag } from "antd";
import { InfoCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    formatDate,
} from "../../../../helpers/genericHelper";
import API from "../../../../services/API";
import CustomText from "../../../CustomText";
import LoaderComponent from '../../../Loader';
import { PersistedCollapse } from '../../../PersistedSections';
import usePageState from '../../../../helpers/usePageState';


export default function PlaidEvents() {
    const { Panel } = Collapse;
    const [plaidEvents, setPlaidEvents] = useState([]);
    const [windowStart, setWindowStart] = useState(null);
    const [nextBefore, setNextBefore] = useState(null);
    const [loading, showLoading] = useState(false);
    const [loadingOlder, showLoadingOlder] = useState(false);
    const [loadedOlder, setLoadedOlder] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = usePageState('plaid-events-rows', []);

    let { userId } = useParams();
    const plaidEventPanel = async () => {
        try {
            showLoading(true);
            let res = await API.getPlaidEvents(userId);
            setPlaidEvents(res.sessions);
            setWindowStart(res.windowStart);
            setNextBefore(res.nextBefore);
        } catch (ex) {
        } finally {
            showLoading(false);
        }
    };

    // The backend serves one time window per request; older sessions come from passing nextBefore back
    const loadOlder = async () => {
        try {
            showLoadingOlder(true);
            let res = await API.getPlaidEvents(userId, nextBefore);
            setPlaidEvents(prev => [...prev, ...res.sessions]);
            setLoadedOlder(true);
            setWindowStart(res.windowStart);
            setNextBefore(res.nextBefore);
        } catch (ex) {
        } finally {
            showLoadingOlder(false);
        }
    };

    // Events without a session id come back as a null-id session on each page, so ids can repeat
    const sessionKey = (session) => session.sessionId ?? `no-session-${session.startTs}`;

    const mainColumns = [
        {
            title: "Start",
            key: "startTs",
            render: (text, record) => CustomText({
                dataSet: record,
                property1: "startTs",
                formatter: (v) => formatDate(v, true)
            })
        },
        {
            title: "ID",
            key: "sessionId",
            render: (text, record) => CustomText({
                dataSet: record,
                property1: "sessionId",
                formatter: (v) => v ? v.slice(0, 6) : "N/A"
            })
        },
        {
            title: "Bank",
            dataIndex: "bank",
            key: "bank"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <>
                    {
                        (() => {
                            let color;
                            switch (status) {
                                case 'Failure':
                                    color = 'volcano';
                                    break;
                                case 'Success':
                                    color = 'green';
                                    break;
                                case 'Incomplete':
                                    color = 'grey';
                                    break;
                                default:
                                    color = 'default';
                            }
                            return (
                                <Tag color={color} key={status}>
                                    {status.toUpperCase()}
                                </Tag>
                            );
                        })()
                    }
                </>
            )
        },
        {
            title: "Exit Status",
            dataIndex: "exitStatus",
            key: "exitStatus"
        },
        {
            title: "Last Error",
            dataIndex: "lastError",
            key: "lastError",
            render: lastError => CustomText({
                dataSet: lastError,
                property1: "code",
                property2: "message",
                seperator: " - "
            })
        }
    ];

    const expandedColumns = [
        {
            title: "End Time",
            dataIndex: "endTs",
            key: "endTs"
        },
        {
            title: "Events",
            dataIndex: "events",
            key: "events",
            render: events => events.join(', ')
        },
        {
            title: "Banks",
            dataIndex: "banks",
            key: "banks",
            render: banks => banks.join(', ')
        },
        {
            title: "Errors",
            dataIndex: "errors",
            key: "errors",
            render: errors => {
                // Filter out null errors and map them, then convert to Set to dedupe
                const uniqueErrors = [...new Set(
                    errors.filter(e => e && e.code && e.message)
                        .map(e => e.code + " - " + e.message)
                )];

                return uniqueErrors.join(', ');
            }
        }
    ];

    const expandedRowRender = (row) => {
        return <Table columns={expandedColumns} dataSource={[row]} pagination={false} />
    };

    useEffect(() => {
        plaidEventPanel();
    }, []);

    return (
        <Row style={{ marginLeft: 20, marginBottom: 20 }}>
            <Col xs={24} xl={24} sm={24} md={24}>
                <PersistedCollapse name="plaid-events-section">
                    <Panel
                        header={
                            <Space>
                                Plaid Events
                                <Tooltip title="Plaid Events track a user's actions and errors when linking their bank account through Plaid.">
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </Space>
                        }
                        key="1"
                    >
                        {loading && <LoaderComponent />}
                        {!loading && <>
                            <Table
                                columns={mainColumns}
                                expandable={{ expandedRowRender, expandedRowKeys, onExpandedRowsChange: setExpandedRowKeys }}
                                dataSource={plaidEvents}
                                rowKey={sessionKey}
                                pagination={false}
                            />
                            {windowStart && (
                                <Space style={{ marginTop: 12 }}>
                                    <span>
                                        {loadedOlder
                                            ? `Showing sessions since ${formatDate(windowStart, true)}`
                                            : "Showing last 90d"}
                                    </span>
                                    {nextBefore && (
                                        <Button onClick={loadOlder} loading={loadingOlder}>
                                            Load older
                                        </Button>
                                    )}
                                </Space>
                            )}
                        </>}
                    </Panel>
                </PersistedCollapse>
            </Col>
        </Row>);
}
